/**
 * Event discussion.
 *
 * Reading needs a session; posting additionally needs a participation, because a
 * message is attributed to a participant nickname. The list is polled by the
 * query hook, so this page owns only presentation and one piece of interaction
 * state: which message the reply box is open under.
 *
 * The server stores a flat list where each row may point at another row, so the
 * page folds it into a thread: a reply is rendered underneath the message it
 * answers, indented by a rule, instead of being repeated at the end of the list.
 * Every message can be collapsed, which is what keeps a long thread readable
 * without hiding the shape of the conversation.
 */

import { useEffect, useState, type ReactNode } from 'react'
import { Link } from 'react-router'
import { useTranslation } from 'react-i18next'
import { ChevronDown } from 'lucide-react'

import type { CommentDto } from '@shared/types'
import type { Locale } from '@shared/locales'

import { AuthGate, useAuthGuard } from '../../components/layout/guards'
import { PageHeader } from '../../components/layout/page-header'
import { Alert, AlertDescription } from '../../components/ui/alert'
import { Button } from '../../components/ui/button'
import { Card, CardContent } from '../../components/ui/card'
import { EmptyState } from '../../components/ui/primitives'
import { CommentComposer } from '../../components/shared/comment-composer'
import { useComments, useMyParticipant, usePostComment, useSharedParticipants } from '../../hooks/use-shared'
import { cn, formatUnixSeconds } from '../../lib/format'
import { currentLocale } from '../../lib/i18n'
import { useSharedOutlet } from './shared-layout'

/** How deep the indentation goes before replies keep the same left edge. */
const MAX_INDENT_DEPTH = 3

/** One message and the replies it received, in the order they were written. */
export interface CommentNode {
  comment: CommentDto
  /** A reply whose parent is not part of this response, so it starts a thread. */
  orphan: boolean
  children: CommentNode[]
}

/**
 * Folds the server's flat list into a thread.
 *
 * A reply whose parent is missing from the response still has to render, so it
 * becomes a root and keeps its "replying to" line as the only context. Children
 * keep the server's chronological order.
 */
export function buildCommentThread(comments: readonly CommentDto[]): CommentNode[] {
  const known = new Set(comments.map((comment) => comment.id))
  const childrenOf = new Map<string, CommentDto[]>()
  const roots: Array<{ comment: CommentDto; orphan: boolean }> = []

  for (const comment of comments) {
    const parentId = comment.replyToId
    if (parentId === null || !known.has(parentId)) {
      roots.push({ comment, orphan: parentId !== null })
      continue
    }
    const list = childrenOf.get(parentId) ?? []
    list.push(comment)
    childrenOf.set(parentId, list)
  }

  function build(comment: CommentDto): CommentNode {
    return {
      comment,
      orphan: false,
      children: (childrenOf.get(comment.id) ?? []).map(build),
    }
  }

  return roots.map((root) => ({ ...root, children: (childrenOf.get(root.comment.id) ?? []).map(build) }))
}

/** Every reply below a node, for the count shown while it is collapsed. */
function countDescendants(node: CommentNode): number {
  return node.children.reduce((total, child) => total + 1 + countDescendants(child), 0)
}

/**
 * Appends the pages a poll returns, newest last and without repeats.
 *
 * A read can overlap what the thread already shows, and a refetch of an older page
 * can settle after a newer one, so the merge is by message id rather than by
 * position.
 */
function mergeComments(current: CommentDto[], incoming: readonly CommentDto[]): CommentDto[] {
  if (incoming.length === 0) {
    return current
  }
  const seen = new Set(current.map((comment) => comment.id))
  const added = incoming.filter((comment) => !seen.has(comment.id))
  return added.length === 0 ? current : [...current, ...added]
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

export function DiscussPage() {
  const { t } = useTranslation()
  const { shareToken, event } = useSharedOutlet()
  const guard = useAuthGuard()
  const participantQuery = useMyParticipant(shareToken)
  const [thread, setThread] = useState<CommentDto[]>([])
  const [cursor, setCursor] = useState<number | null>(null)
  // The first read is the newest page, so a thread longer than one page opens at the
  // conversation. Every read after that asks for what arrived since the newest
  // message the page already holds, which is what makes new messages visible at all.
  const commentsQuery = useComments(shareToken, { after: cursor, tail: cursor === null })
  const participantsQuery = useSharedParticipants(shareToken)
  const postComment = usePostComment(shareToken)

  const locale = currentLocale()
  const [replyToId, setReplyToId] = useState<string | null>(null)

  useEffect(() => {
    const page = commentsQuery.data
    if (!page) {
      return
    }
    setThread((current) => mergeComments(current, page.comments))
    if (page.cursor !== null && page.cursor !== cursor) {
      setCursor(page.cursor)
    }
  }, [commentsQuery.data, cursor])

  const comments = thread
  const archived = event.status === 'archived'
  const canPost = Boolean(participantQuery.data) && !archived
  const participants = participantsQuery.data?.participants ?? []
  const roots = buildCommentThread(comments)

  /** Wraps every mentioned nickname in an emphasised span. */
  function renderContent(comment: CommentDto) {
    if (comment.mentionedNames.length === 0) {
      return comment.content
    }
    const pattern = new RegExp(`(@(?:${comment.mentionedNames.map(escapeRegExp).join('|')}))`, 'g')
    return comment.content.split(pattern).map((part, index) =>
      comment.mentionedNames.some((name) => part === `@${name}`) ? (
        <span key={`${comment.id}-${index}`} className="text-primary font-medium">
          {part}
        </span>
      ) : (
        part
      ),
    )
  }

  function submit(content: string, replyTo: string | null): Promise<unknown> {
    // The composer clears itself only when this settles successfully, so the member
    // keeps their text when the post is refused.
    return postComment.mutateAsync(
      { content, replyToId: replyTo },
      {
        onSuccess: () => {
          setReplyToId(null)
        },
      },
    )
  }

  // The shared gate keeps this page on the same waiting, failure and sign-in
  // redirect branches as every other session-only route.
  if (guard.status !== 'allowed') {
    return <AuthGate guard={guard} />
  }

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        breadcrumbs={[
          { label: t('nav.home'), to: '/' },
          { label: event.title, to: `/e/${shareToken}` },
          { label: t('discuss.title') },
        ]}
        title={t('discuss.title')}
      />

      <Card>
        <CardContent className="pt-4 sm:pt-5">
          {commentsQuery.isError ? (
            <Alert variant="destructive">
              <AlertDescription>
                {t('discuss.loadFailed')}{' '}
                <button type="button" className="underline" onClick={() => void commentsQuery.refetch()}>
                  {t('common.retry')}
                </button>
              </AlertDescription>
            </Alert>
          ) : roots.length === 0 ? (
            <EmptyState title={t('discuss.empty')} />
          ) : (
            <ul className="flex flex-col gap-4">
              {roots.map((node) => (
                <CommentThread
                  key={node.comment.id}
                  node={node}
                  depth={0}
                  locale={locale}
                  canPost={canPost}
                  participants={participants}
                  replyToId={replyToId}
                  replySending={postComment.isPending}
                  replyError={replyToId !== null && postComment.isError ? postComment.error : null}
                  onReply={setReplyToId}
                  onSubmit={submit}
                  renderContent={renderContent}
                />
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {canPost ? (
        <Card>
          <CardContent className="pt-4 sm:pt-5">
            <CommentComposer
              candidates={participants}
              placeholder={t('discuss.placeholder')}
              submitLabel={t('discuss.send')}
              pendingLabel={t('discuss.sending')}
              isPending={postComment.isPending && replyToId === null}
              error={replyToId === null ? postComment.error : null}
              onSubmit={(content) => submit(content, null)}
            />
          </CardContent>
        </Card>
      ) : (
        <Alert variant="info">
          {archived ? (
            <AlertDescription>{t('discuss.archivedHint')}</AlertDescription>
          ) : (
            <AlertDescription>
              {t('discuss.notJoined')}{' '}
              <Link to={`/e/${shareToken}/join/location`} className="underline">
                {t('common.join')}
              </Link>
            </AlertDescription>
          )}
        </Alert>
      )}
    </div>
  )
}

interface CommentThreadProps {
  node: CommentNode
  depth: number
  locale: Locale
  canPost: boolean
  participants: readonly { id: string; nickname: string }[]
  replyToId: string | null
  replySending: boolean
  replyError: Error | null
  onReply: (id: string | null) => void
  onSubmit: (content: string, replyTo: string | null) => void | Promise<unknown>
  renderContent: (comment: CommentDto) => ReactNode
}

/**
 * One message and its replies.
 *
 * Collapsing hides the body and every reply while keeping the author line, so a
 * member can fold away a branch that does not concern them and still see that it
 * exists and how big it is.
 */
function CommentThread({
  node,
  depth,
  locale,
  canPost,
  participants,
  replyToId,
  replySending,
  replyError,
  onReply,
  onSubmit,
  renderContent,
}: CommentThreadProps) {
  const { t } = useTranslation()
  const [collapsed, setCollapsed] = useState(false)
  const descendants = countDescendants(node)
  const replying = replyToId === node.comment.id

  return (
    <li className="flex gap-2">
      <button
        type="button"
        aria-expanded={!collapsed}
        aria-label={collapsed ? t('discuss.expand') : t('discuss.collapse')}
        className="text-muted-foreground hover:text-foreground flex size-6 shrink-0 items-center justify-center rounded-md"
        onClick={() => setCollapsed((value) => !value)}
      >
        <ChevronDown className={cn('size-4 transition-transform', collapsed && '-rotate-90')} aria-hidden />
      </button>

      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex flex-wrap items-baseline gap-2">
          <span className="text-sm font-medium">{node.comment.nickname}</span>
          <span className="text-muted-foreground text-xs">
            {formatUnixSeconds(node.comment.createdAt, locale)}
          </span>
          {collapsed && descendants > 0 ? (
            <span className="text-muted-foreground text-xs">
              {t('discuss.replies', { count: descendants })}
            </span>
          ) : null}
        </div>

        {collapsed ? null : (
          <>
            {node.orphan && node.comment.replyToNickname ? (
              <p className="text-muted-foreground text-xs">
                {t('discuss.reply', { name: node.comment.replyToNickname })}
              </p>
            ) : null}

            <p className="text-sm break-words whitespace-pre-wrap">{renderContent(node.comment)}</p>

            {canPost ? (
              <div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2"
                  onClick={() => onReply(replying ? null : node.comment.id)}
                >
                  {t('discuss.replyAction')}
                </Button>
              </div>
            ) : null}

            {replying ? (
              <div className="border-border bg-muted/30 mt-1 rounded-md border p-3">
                <CommentComposer
                  candidates={participants}
                  placeholder={t('discuss.replyPlaceholder', { name: node.comment.nickname })}
                  submitLabel={t('discuss.send')}
                  pendingLabel={t('discuss.sending')}
                  isPending={replySending}
                  error={replyError}
                  onSubmit={(content) => onSubmit(content, node.comment.id)}
                  onCancel={() => onReply(null)}
                  autoFocus
                />
              </div>
            ) : null}

            {node.children.length > 0 ? (
              <ul
                className={cn(
                  'flex flex-col gap-4',
                  depth < MAX_INDENT_DEPTH && 'border-border mt-2 border-l pl-3',
                )}
              >
                {node.children.map((child) => (
                  <CommentThread
                    key={child.comment.id}
                    node={child}
                    depth={depth + 1}
                    locale={locale}
                    canPost={canPost}
                    participants={participants}
                    replyToId={replyToId}
                    replySending={replySending}
                    replyError={replyError}
                    onReply={onReply}
                    onSubmit={onSubmit}
                    renderContent={renderContent}
                  />
                ))}
              </ul>
            ) : null}
          </>
        )}
      </div>
    </li>
  )
}
